<mat-progress-bar [mode]="loading() ? 'indeterminate' : 'determinate'" [value]="100" class="page-progress" aria-label="Loading"></mat-progress-bar>

<lib-page-header [title]="type()" screenName="UserData Type"
                  [backTo]="backTo()" [backLabel]="backLabel()">
    <mat-icon icon>schema</mat-icon>
    <ng-container stats *ngIf="detail() as d">
        <div class="page-header-stat">
            <mat-icon>view_list</mat-icon>
            <span class="stat-value">{{ d.rowCount }}</span>
            <span class="stat-label">Rows</span>
        </div>
        <div class="page-header-stat">
            <mat-icon>tag</mat-icon>
            <span class="stat-value">{{ d.attributeCount }}</span>
            <span class="stat-label">Attributes</span>
        </div>
        <div class="page-header-stat">
            <mat-icon>link</mat-icon>
            <span class="stat-value">{{ d.templateInCount }}</span>
            <span class="stat-label">Uses</span>
        </div>
    </ng-container>
    <ng-container actions>
        <!-- Type-options actions moved from the toolbar to the page-header so
             the toolbar can dedicate its full width to the three field
             columns below. Reset reverts the draft; Save persists it; Help
             toggles the right-column aside. -->
        <button mat-stroked-button type="button"
                class="page-header-secondary-action"
                [disabled]="!isOptionsDirty() || savingOptions()"
                (click)="onResetOptions()"
                matTooltip="Revert unsaved changes">
            <mat-icon>undo</mat-icon>
            <span>Reset</span>
        </button>
        <button mat-flat-button color="primary" type="button"
                *ngIf="canManage()"
                class="page-header-primary-action"
                [disabled]="!isOptionsDirty() || savingOptions()"
                (click)="onSaveOptions()"
                matTooltip="Save type options">
            <mat-icon>save</mat-icon>
            <span>Save</span>
        </button>
        <button mat-icon-button type="button"
                (click)="toggleHelp()"
                [matTooltip]="helpCollapsed() ? 'Show help' : 'Hide help'"
                [attr.aria-label]="helpCollapsed() ? 'Show help' : 'Hide help'"
                [attr.aria-expanded]="!helpCollapsed()"
                class="help-toggle"
                [class.active]="!helpCollapsed()">
            <mat-icon>{{ helpCollapsed() ? 'help_outline' : 'help' }}</mat-icon>
        </button>
    </ng-container>
</lib-page-header>

<section class="main-content detail-content">

    <!-- Type-level options TOOLBAR — sticky across every tab. Holds the
         strict-mode toggle + UserData-v2 resource / cost / manual defaults
         (see Confluence design page 82345987 §7.6). The trailing help button
         toggles the collapsible drawer below; drawer content swaps with the
         active tab. -->
    <!-- Type-options toolbar — single panel, three columns. Save/Reset/Help
         live in the page-header actions above. Each column owns one
         configuration concern; controls are stacked vertically inside. -->
    <div class="type-toolbar app-panel" *ngIf="draftOptions() as draft">
        <div class="toolbar-grid">

            <!-- Column 1 — Schema + Manual writes -->
            <div class="toolbar-col">
                <section class="toolbar-section">
                    <span class="group-label">Schema</span>
                    <mat-slide-toggle color="primary"
                                      [checked]="draft.strict"
                                      [disabled]="savingOptions() || !canManage()"
                                      (change)="onToggleStrict($event.checked)"
                                      matTooltip="Lock the type's shape — undeclared fields are ignored on ingest">
                        <span class="control-label">Strict</span>
                    </mat-slide-toggle>
                </section>

                <section class="toolbar-section">
                    <span class="group-label">Manual writes</span>
                    <mat-slide-toggle color="primary"
                                      [checked]="draft.manualCreate === true"
                                      [disabled]="savingOptions() || !canManage()"
                                      (change)="onToggleManualCreate($event.checked)"
                                      matTooltip="Allow ad-hoc UI creation of rows of this type">
                        <span class="control-label">Create</span>
                    </mat-slide-toggle>
                    <mat-slide-toggle color="primary"
                                      [checked]="draft.manualEdit === true"
                                      [disabled]="savingOptions() || !canManage()"
                                      (change)="onToggleManualEdit($event.checked)"
                                      matTooltip="Allow UI editing of existing rows">
                        <span class="control-label">Edit</span>
                    </mat-slide-toggle>
                </section>
            </div>

            <!-- Column 2 — Resource defaults -->
            <div class="toolbar-col">
                <section class="toolbar-section">
                    <span class="group-label">Resource defaults</span>
                    <mat-slide-toggle color="primary"
                                      [checked]="draft.resourceDefault === true"
                                      [disabled]="savingOptions() || !canManage()"
                                      (change)="onToggleResourceDefault($event.checked)"
                                      matTooltip="Rows of this type default to being resources (per-row override still wins)">
                        <span class="control-label">Is resource</span>
                    </mat-slide-toggle>
                    <mat-slide-toggle color="primary"
                                      [checked]="draft.reusableDefault === true"
                                      [disabled]="savingOptions() || !canManage()"
                                      (change)="onToggleReusableDefault($event.checked)"
                                      matTooltip="Pool (true) vs burn-down (false) capacity semantics">
                        <span class="control-label">Reusable</span>
                    </mat-slide-toggle>
                    <mat-form-field appearance="outline" class="toolbar-field full-width">
                        <mat-label>Capacity unit</mat-label>
                        <mat-icon matIconPrefix class="field-icon">straighten</mat-icon>
                        <input matInput
                               type="text"
                               [value]="draft.resourceCapacityUnit ?? ''"
                               [disabled]="savingOptions() || !canManage()"
                               (change)="onCapacityUnitChange($any($event.target).value)"
                               placeholder="e.g. slots"
                               matTooltip="UI label for the row's capacity number"
                               maxlength="32">
                    </mat-form-field>
                </section>
            </div>

            <!-- Column 3 — Cost defaults -->
            <div class="toolbar-col">
                <section class="toolbar-section">
                    <span class="group-label">Cost defaults</span>
                    <mat-form-field appearance="outline" class="toolbar-field full-width">
                        <mat-label>Per use</mat-label>
                        <mat-icon matIconPrefix class="field-icon">payments</mat-icon>
                        <input matInput
                               type="number"
                               step="0.01"
                               min="0"
                               [value]="draft.costPerUseDefault ?? ''"
                               [disabled]="savingOptions() || !canManage()"
                               (change)="onCostDefaultChange('costPerUseDefault',
                                                            $any($event.target).valueAsNumber || null)"
                               matTooltip="Default cost charged per claim">
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="toolbar-field full-width">
                        <mat-label>Currency</mat-label>
                        <mat-icon matIconPrefix class="field-icon">paid</mat-icon>
                        <mat-select [value]="draft.costCurrencyDefault ?? null"
                                    [disabled]="savingOptions() || !canManage()"
                                    (selectionChange)="onCostCurrencyChange($event.value)"
                                    matTooltip="ISO 4217 currency code">
                            <mat-option [value]="null">
                                <em>(none)</em>
                            </mat-option>
                            <mat-option *ngFor="let code of CURRENCY_CODES" [value]="code">{{ code }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="toolbar-field full-width">
                        <mat-label>Monthly warn</mat-label>
                        <mat-icon matIconPrefix class="field-icon">warning_amber</mat-icon>
                        <input matInput
                               type="number"
                               step="1"
                               min="0"
                               [value]="draft.costWarningThresholdMonthly ?? ''"
                               [disabled]="savingOptions() || !canManage()"
                               (change)="onCostDefaultChange('costWarningThresholdMonthly',
                                                            $any($event.target).valueAsNumber || null)"
                               matTooltip="Soft monthly cost ceiling — schedule wizard flags projections that exceed it">
                    </mat-form-field>
                </section>
            </div>
        </div>
    </div>

    <!-- 70/30 body split — tabs panel on the left, contextual help aside
         on the right. When the help toggle in the toolbar is off, the help
         aside is hidden and the tabs panel claims 100% of the width. -->
    <div class="detail-body" [class.help-open]="!helpCollapsed()">

    <!-- Single elevated panel wraps the tab strip + body so every tab's
         content sits inside the same chrome (border / radius / shadow) —
         same canonical pattern as the User detail page. -->
    <div class="detail-tabs-wrapper app-panel">
    <mat-tab-group mat-stretch-tabs="false" animationDuration="0ms" class="detail-tabs"
                   [selectedIndex]="activeTabIndex()"
                   (selectedIndexChange)="activeTabIndex.set($event)">

        <!-- ============================================================ -->
        <!-- TAB 1: ATTRIBUTES                                             -->
        <!-- ============================================================ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">tag</mat-icon>
                Attributes
                <span class="tab-count" *ngIf="attributes().length > 0">({{ attributes().length }})</span>
            </ng-template>

            <div class="tab-body">
                <div class="action-row">
                    <button mat-flat-button color="primary" *ngIf="canManage()" (click)="openCreateAttribute()">
                        <mat-icon>add</mat-icon>
                        Add manual attribute
                    </button>
                    <span class="flex-spacer"></span>
                    <button mat-stroked-button *ngIf="canManage()" (click)="reExtractAttributes()" [disabled]="isEmpty()"
                            [matTooltip]="isEmpty()
                                ? 'No rows of this type to re-extract'
                                : 'Re-run extraction across every row of this type'">
                        <mat-icon>autorenew</mat-icon>
                        Re-extract attributes for all rows
                    </button>
                </div>

                <div class="table-wrapper" *ngIf="attributes().length > 0">
                    <table mat-table [dataSource]="attributes()" class="data-table">

                        <ng-container matColumnDef="name">
                            <th mat-header-cell *matHeaderCellDef>Name</th>
                            <td mat-cell *matCellDef="let a">
                                <!-- Save-in-progress disables the link so we don't open
                                     the edit dialog mid-write — same guard as the
                                     inline editors and the (former) edit-icon. -->
                                <a class="traversable"
                                   [class.disabled]="isAttributeSaving(a.name) || !canManage()"
                                   (click)="canManage() && !isAttributeSaving(a.name) && openEditAttribute(a)">{{ a.name }}</a>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="payloadPath">
                            <th mat-header-cell *matHeaderCellDef>Payload path</th>
                            <td mat-cell *matCellDef="let a"><code class="path-mono">{{ a.payloadPath }}</code></td>
                        </ng-container>

                        <ng-container matColumnDef="valueType">
                            <th mat-header-cell *matHeaderCellDef>Value type</th>
                            <td mat-cell *matCellDef="let a">
                                <mat-form-field appearance="outline" class="inline-field">
                                    <mat-select [value]="a.valueType"
                                                (selectionChange)="setAttributeValueType(a, $event.value)"
                                                [disabled]="isAttributeSaving(a.name) || !canManage()">
                                        <mat-option *ngFor="let v of VALUE_TYPES" [value]="v">{{ v }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="indexed">
                            <th mat-header-cell *matHeaderCellDef>Indexed</th>
                            <td mat-cell *matCellDef="let a">
                                <mat-slide-toggle [checked]="a.indexed"
                                                  (change)="setAttributeIndexed(a, $event.checked)"
                                                  [disabled]="isAttributeSaving(a.name) || !canManage()"></mat-slide-toggle>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="readOnly">
                            <th mat-header-cell *matHeaderCellDef>Read-only</th>
                            <td mat-cell *matCellDef="let a">
                                <mat-icon *ngIf="a.readOnly === true; else readOnlyOff"
                                          class="readonly-on"
                                          matTooltip="Field is locked on UserData edit screens">lock</mat-icon>
                                <ng-template #readOnlyOff>
                                    <mat-icon class="readonly-off"
                                              matTooltip="Editable">lock_open</mat-icon>
                                </ng-template>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="source">
                            <th mat-header-cell *matHeaderCellDef>Source</th>
                            <td mat-cell *matCellDef="let a">
                                <span [class]="sourceChipClass(a.source)">
                                    <mat-icon class="chip-icon">
                                        {{ a.source === 'MANUAL' ? 'lock' : 'auto_awesome' }}
                                    </mat-icon>
                                    {{ a.source }}
                                </span>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="sampleCount">
                            <th mat-header-cell *matHeaderCellDef class="col-numeric">Samples</th>
                            <td mat-cell *matCellDef="let a" class="col-numeric"
                                [matTooltip]="a.lastSeenAt ? 'Last seen ' + (a.lastSeenAt | date:'medium') : ''">
                                {{ a.sampleCount ?? 0 }}
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="star">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let a" class="action-cell">
                                <button mat-icon-button *ngIf="canManage()" (click)="toggleAttributeLock(a)"
                                        [disabled]="isAttributeSaving(a.name)"
                                        [matTooltip]="a.source === 'MANUAL' ? 'Unlock to inferred' : 'Lock as manual'"
                                        [attr.aria-label]="a.source === 'MANUAL' ? 'Unlock attribute to inferred' : 'Lock attribute as manual'">
                                    <mat-icon>{{ a.source === 'MANUAL' ? 'lock_open' : 'lock' }}</mat-icon>
                                </button>
                                <!-- Edit moved to the clickable Name column. -->
                                <button mat-icon-button *ngIf="canManage()" (click)="deleteAttribute(a)"
                                        [disabled]="isAttributeSaving(a.name)"
                                        matTooltip="Delete" aria-label="Delete attribute">
                                    <mat-icon>delete</mat-icon>
                                </button>
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="attributeColumns; sticky: true"></tr>
                        <tr mat-row *matRowDef="let row; columns: attributeColumns"></tr>
                    </table>
                </div>

                <div class="empty-state" *ngIf="!loading() && attributes().length === 0">
                    <mat-icon class="empty-icon">tag</mat-icon>
                    <h3>No attributes yet</h3>
                    <p>Attributes are inferred automatically from ingested rows of <strong>{{ type() }}</strong>,
                       or you can add them manually with the button above.</p>
                </div>
            </div>
        </mat-tab>

        <!-- ============================================================ -->
        <!-- TAB 2: TEMPLATES                                              -->
        <!-- ============================================================ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">link</mat-icon>
                Templates
                <span class="tab-count" *ngIf="templates().length + incomingTemplates().length > 0">
                    ({{ templates().length + incomingTemplates().length }})
                </span>
            </ng-template>

            <div class="tab-body">
                <!-- Outgoing: this type uses other types as templates -->
                <div class="section-block">
                    <div class="section-header">
                        <mat-icon>arrow_upward</mat-icon>
                        <h4>Templates this type uses</h4>
                        <span class="section-sub">Rules with source_type = <code>{{ type() }}</code></span>
                        <span class="flex-spacer"></span>
                        <button mat-flat-button color="primary" *ngIf="canManage()" (click)="openCreateTemplate()">
                            <mat-icon>add</mat-icon>
                            Add manual template
                        </button>
                    </div>

                    <div class="table-wrapper" *ngIf="templates().length > 0">
                        <table mat-table [dataSource]="templates()" class="data-table">

                            <ng-container matColumnDef="referencePath">
                                <th mat-header-cell *matHeaderCellDef>Reference path</th>
                                <td mat-cell *matCellDef="let t">
                                    <!-- Most identifying column → carries the edit click. Save-
                                         in-progress disables it (same guard as the (former)
                                         edit-icon button). -->
                                    <a class="traversable"
                                       [class.disabled]="isTemplateSaving(t.targetPath) || !canManage()"
                                       (click)="canManage() && !isTemplateSaving(t.targetPath) && openEditTemplate(t)">
                                        <code class="path-mono">{{ t.referencePath }}</code>
                                    </a>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="mapping">
                                <th mat-header-cell *matHeaderCellDef>Template &rarr; field</th>
                                <td mat-cell *matCellDef="let t">
                                    <code class="path-mono">{{ formatTemplateMapping(t) }}</code>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="targetPath">
                                <th mat-header-cell *matHeaderCellDef>Target</th>
                                <td mat-cell *matCellDef="let t"><code class="path-mono">{{ t.targetPath }}</code></td>
                            </ng-container>

                            <ng-container matColumnDef="overwrite">
                                <th mat-header-cell *matHeaderCellDef>Overwrite</th>
                                <td mat-cell *matCellDef="let t">
                                    <mat-slide-toggle [checked]="t.overwrite"
                                                      (change)="setTemplateOverwrite(t, $event.checked)"
                                                      [disabled]="isTemplateSaving(t.targetPath) || !canManage()"></mat-slide-toggle>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="source">
                                <th mat-header-cell *matHeaderCellDef>Source</th>
                                <td mat-cell *matCellDef="let t">
                                    <span [class]="sourceChipClass(t.source)">
                                        <mat-icon class="chip-icon">
                                            {{ t.source === 'MANUAL' ? 'lock' : 'auto_awesome' }}
                                        </mat-icon>
                                        {{ t.source }}
                                    </span>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="confidence">
                                <th mat-header-cell *matHeaderCellDef class="col-numeric">Confidence</th>
                                <td mat-cell *matCellDef="let t" class="col-numeric">{{ confidencePercent(t.confidence) }}</td>
                            </ng-container>

                            <ng-container matColumnDef="sampleCount">
                                <th mat-header-cell *matHeaderCellDef class="col-numeric">Samples</th>
                                <td mat-cell *matCellDef="let t" class="col-numeric"
                                    [matTooltip]="t.lastInferredAt ? 'Last inferred ' + (t.lastInferredAt | date:'medium') : ''">
                                    {{ t.sampleCount ?? 0 }}
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="star">
                                <th mat-header-cell *matHeaderCellDef></th>
                                <td mat-cell *matCellDef="let t" class="action-cell">
                                    <button mat-icon-button *ngIf="canManage()" (click)="toggleTemplateLock(t)"
                                            [disabled]="isTemplateSaving(t.targetPath)"
                                            [matTooltip]="t.source === 'MANUAL' ? 'Unlock to inferred' : 'Lock as manual'"
                                            [attr.aria-label]="t.source === 'MANUAL' ? 'Unlock template to inferred' : 'Lock template as manual'">
                                        <mat-icon>{{ t.source === 'MANUAL' ? 'lock_open' : 'lock' }}</mat-icon>
                                    </button>
                                    <!-- Edit moved to the clickable Reference-path column. -->
                                    <button mat-icon-button *ngIf="canManage()" (click)="deleteTemplate(t)"
                                            [disabled]="isTemplateSaving(t.targetPath)"
                                            matTooltip="Delete" aria-label="Delete template">
                                        <mat-icon>delete</mat-icon>
                                    </button>
                                </td>
                            </ng-container>

                            <tr mat-header-row *matHeaderRowDef="templateColumns; sticky: true"></tr>
                            <tr mat-row *matRowDef="let row; columns: templateColumns"></tr>
                        </table>
                    </div>

                    <div class="empty-state inline" *ngIf="templates().length === 0">
                        <mat-icon>link_off</mat-icon>
                        <p>No outgoing template rules for this type yet.</p>
                    </div>
                </div>

                <!-- Incoming: other types reference this one as a template -->
                <div class="section-block">
                    <div class="section-header">
                        <mat-icon>arrow_downward</mat-icon>
                        <h4>Types that reference this type as a template</h4>
                        <span class="section-sub">Rules with template_type = <code>{{ type() }}</code></span>
                    </div>

                    <div class="table-wrapper" *ngIf="incomingTemplates().length > 0">
                        <table mat-table [dataSource]="incomingTemplates()" class="data-table">

                            <ng-container matColumnDef="referencePath">
                                <th mat-header-cell *matHeaderCellDef>Reference path (in source)</th>
                                <td mat-cell *matCellDef="let t"><code class="path-mono">{{ t.referencePath }}</code></td>
                            </ng-container>

                            <ng-container matColumnDef="targetType">
                                <th mat-header-cell *matHeaderCellDef>Source type</th>
                                <td mat-cell *matCellDef="let t">{{ t.sourceType }}</td>
                            </ng-container>

                            <ng-container matColumnDef="targetPath">
                                <th mat-header-cell *matHeaderCellDef>Target path</th>
                                <td mat-cell *matCellDef="let t"><code class="path-mono">{{ t.targetPath }}</code></td>
                            </ng-container>

                            <ng-container matColumnDef="overwrite">
                                <th mat-header-cell *matHeaderCellDef>Overwrite</th>
                                <td mat-cell *matCellDef="let t">
                                    <mat-icon *ngIf="t.overwrite" class="bool-on">check</mat-icon>
                                    <mat-icon *ngIf="!t.overwrite" class="bool-off">remove</mat-icon>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="source">
                                <th mat-header-cell *matHeaderCellDef>Source</th>
                                <td mat-cell *matCellDef="let t">
                                    <span [class]="sourceChipClass(t.source)">
                                        <mat-icon class="chip-icon">
                                            {{ t.source === 'MANUAL' ? 'lock' : 'auto_awesome' }}
                                        </mat-icon>
                                        {{ t.source }}
                                    </span>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="confidence">
                                <th mat-header-cell *matHeaderCellDef class="col-numeric">Confidence</th>
                                <td mat-cell *matCellDef="let t" class="col-numeric">{{ confidencePercent(t.confidence) }}</td>
                            </ng-container>

                            <tr mat-header-row *matHeaderRowDef="incomingTemplateColumns; sticky: true"></tr>
                            <tr mat-row *matRowDef="let row; columns: incomingTemplateColumns"></tr>
                        </table>
                    </div>

                    <div class="empty-state inline" *ngIf="incomingTemplates().length === 0">
                        <mat-icon>link_off</mat-icon>
                        <p>No other types use <strong>{{ type() }}</strong> as a template.</p>
                    </div>
                </div>
            </div>
        </mat-tab>

        <!-- ============================================================ -->
        <!-- TAB 3: SAMPLE DATA                                            -->
        <!-- ============================================================ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">data_object</mat-icon>
                Sample data
                <span class="tab-count" *ngIf="(detail()?.recentRows?.length ?? 0) > 0">
                    ({{ detail()?.recentRows?.length }})
                </span>
            </ng-template>

            <div class="tab-body">
                <div class="sample-list" *ngIf="(detail()?.recentRows?.length ?? 0) > 0">
                    <div class="sample-card" *ngFor="let row of detail()?.recentRows">
                        <div class="sample-header" (click)="toggleSample(row.key)">
                            <mat-icon class="sample-toggle"
                                      [class.sample-toggle-collapsed]="!isSampleExpanded(row.key)">
                                expand_more
                            </mat-icon>
                            <div class="sample-id">
                                <mat-icon>fingerprint</mat-icon>
                                <code class="path-mono">{{ row.key }}</code>
                                <span class="sample-meta" *ngIf="row.label"> · {{ row.label }}</span>
                            </div>
                            <span class="sample-meta" *ngIf="row.createdAt">
                                {{ row.createdAt | date:'medium' }}
                            </span>
                        </div>
                        @if (isSampleExpanded(row.key)) {
                            <div class="sample-payload">
                                <app-json-path-viewer [value]="row.payload"></app-json-path-viewer>
                            </div>
                        }
                    </div>
                </div>

                <div class="empty-state" *ngIf="!loading() && (detail()?.recentRows?.length ?? 0) === 0">
                    <mat-icon class="empty-icon">data_object</mat-icon>
                    <h3>No sample data yet</h3>
                    <p>No rows of <strong>{{ type() }}</strong> have been ingested yet.</p>
                </div>
            </div>
        </mat-tab>

    </mat-tab-group>
    </div>

    <!-- Right column — contextual help. Content swaps with the active tab,
         plus an always-on "Toolbar defaults" guide so the v2 toolbar
         controls have inline documentation. Hidden when helpCollapsed
         flips on. Standard .app-panel chrome wraps the help-body sub-cards
         so the column reads as a sibling of the tabs panel. -->
    <aside class="help-panel app-panel" *ngIf="!helpCollapsed()">

        <ng-container [ngSwitch]="activeTabIndex()">

            <!-- TAB 0 — Attributes help -->
            <div class="help-body" *ngSwitchCase="0">
                <mat-icon class="help-icon">info</mat-icon>
                <div>
                    <strong>Attributes</strong> are searchable fields the platform extracts from each
                    row's JSON payload into a queryable index. Every scalar leaf in a payload
                    (e.g. <code>vendor</code>, <code>firmware</code>) becomes one or more rows in
                    the attribute table.
                    <ul class="help-list">
                        <li><strong>INFERRED</strong> rules emerge automatically as new payload fields
                            are observed (lenient mode). Lock a rule as <strong>MANUAL</strong> to fix
                            its value-type.</li>
                        <li><strong>Indexed</strong> off keeps the rule alive but stops emitting attribute
                            rows for that field.</li>
                        <li><strong>Re-extract</strong> reprocesses every existing row of this type with
                            the current rules + templates.</li>
                        <li><strong>Toolbar &gt; Schema &gt; Strict</strong> locks the contract: only declared
                            attributes get extracted.</li>
                    </ul>
                </div>
            </div>

            <!-- TAB 1 — Templates help -->
            <div class="help-body" *ngSwitchCase="1">
                <mat-icon class="help-icon">info</mat-icon>
                <div>
                    <strong>Templates</strong> copy fields from <em>another</em> row onto a row of
                    this type at upsert time.
                    <ul class="help-list">
                        <li>Use when shared metadata lives elsewhere — e.g. <code>NE</code> rows
                            reference <code>EQUIPMENT_TYPE</code> for vendor / model / OS.</li>
                        <li>Denormalization happens at <strong>write time</strong> — queries stay fast.</li>
                        <li>Two views: <em>Templates this type uses</em> (incoming) and
                            <em>Templates that reference this type</em> (outgoing, read-only).</li>
                    </ul>
                </div>
            </div>

            <!-- TAB 2 — Sample data help -->
            <div class="help-body" *ngSwitchCase="2">
                <mat-icon class="help-icon">info</mat-icon>
                <div>
                    <strong>Sample data</strong> is a peek at the most recent
                    <strong>{{ detail()?.recentRows?.length ?? 0 }}</strong>
                    {{ ((detail()?.recentRows?.length ?? 0) === 1) ? 'row' : 'rows' }}
                    of <code>{{ type() }}</code> with full JSON payloads. Use the keys you see here
                    to author <em>Attribute</em> or <em>Template</em> rules.
                </div>
            </div>

        </ng-container>

        <!-- Toolbar guidance — always visible regardless of active tab. -->
        <div class="help-body help-toolbar-guide">
            <mat-icon class="help-icon">tune</mat-icon>
            <div>
                <strong>Toolbar defaults</strong> apply to every new row of this type, but each
                row may override:
                <ul class="help-list">
                    <li><strong>Is resource</strong> + <strong>Reusable</strong> + <strong>Capacity unit</strong>
                        seed the resource-card editor. <em>Pool</em> replenishes on release;
                        <em>burn-down</em> consumes one unit per claim.</li>
                    <li><strong>Per use</strong> + <strong>Currency</strong> seed cost capture.
                        <strong>Monthly warn</strong> is a soft ceiling for the schedule wizard.</li>
                    <li><strong>Manual writes</strong> — by default rows of this type are API-only.
                        Toggle Create / Edit on to let operators author rows by hand.</li>
                </ul>
            </div>
        </div>
    </aside>

    </div><!-- /.detail-body -->
</section>
